import React, { useRef } from 'react';
import { NewsStory, NewsStoryProps } from './NewsStory';
import { FeaturedNewsLayout } from './FeaturedNewsLayout';
import { NewsGridLayout } from './NewsGridLayout';
import { SingleNewsLayout } from './SingleNewsLayout';
import { motion } from 'framer-motion';
import { CTATiles } from './blocks/CTATiles';
import Disclaimer, { DisclaimerProps } from './Disclaimer';
import { CTAList, useScrolledTheme, SplitH1, SplitH2, CTA, useSanitizedTranslation, XumoButton as Button } from 'ui';

export type NewsBlockProps = {
  heading: string;
  body?: string;
  stories: NewsStoryProps[];
  background_color?: string;
  accent_color?: string;
  headingOne?: boolean;
  tags: any[];
  cta?: CTA[];
  section_padding?: string;
};

const variants = {
  initial: (i: number) => ({
    opacity: 0,
    x: -40,
  }),
  animate: (i: number) => ({
    x: 0,
    y: 0,
    opacity: 1,
    transition: { staggerChildren: 0.075, ease: 'easeInOut', duration: 0.5 },
  }),
};

export const NewsBlock = ({
  heading = "What's new with Xumo",
  body,
  stories,
  cta,
  tags,
  headingOne,
  background_color = '#ffffff',
  accent_color = 'xumoAltBerryLight',
  section_padding,
}: NewsBlockProps) => {
  const { t } = useSanitizedTranslation();
  const darkBackground = background_color === '#231520' || background_color === '#140D13';

  const accentColor = accent_color || 'xumoAltBerryLight';

  return (
    <>
      <section
        id="news-section"
        className={`relative z-30 flex w-full flex-col items-start justify-center py-8 ${section_padding ? section_padding : 'lg:py-16'}`}
        style={{ background: background_color || '#ffffff' }}
      >
        <div className="wrapper">
          <h2
            data-i18n={t(heading.replace(/'/g, '&rsquo;'))}
            className={`type-heading-small pb-4 ${stories.length > 3 ? 'text-left' : 'text-center'} ${darkBackground ? 'text-white' : 'text-black'}`}
            dangerouslySetInnerHTML={{ __html: t(heading.replace(/'/g, '&rsquo;')) }}
          />
          {stories.length > 3 && <hr className="mb-8 border-gray-300" />}
          {body && <p className={`type-body weight-300 text-[20px]`} data-i18n={body} dangerouslySetInnerHTML={{ __html: t(body) }} />}
          <motion.div variants={variants} initial="initial" animate="animate" id="gallery" className="flex flex-col gap-8 py-6">
            {/* Only 3 stories */}
            {stories.length <= 3 && <NewsGridLayout stories={stories.slice(0, 3)} accent={accentColor} />}
            {/* First 4 stories */}
            {stories.length >= 4 && <FeaturedNewsLayout stories={stories.slice(0, 4)} accent={accentColor} />}
            {/* Next 6 stories */}
            {stories.length > 4 && <NewsGridLayout stories={stories.slice(4, 10)} accent={accentColor} fullpage />}
            {/* Next single one */}
            {stories.length > 10 && <SingleNewsLayout story={stories[10]} accent={accentColor} />}
            {/* Da rest! */}
            {stories.length > 11 &&
              (() => {
                const remainingStories = stories.slice(11);
                const layouts = [];
                let currentIndex = 0;

                while (currentIndex < remainingStories.length) {
                  // For the gird of 6
                  if (currentIndex < remainingStories.length) {
                    const gridStories = remainingStories.slice(currentIndex, currentIndex + 6);
                    layouts.push(<NewsGridLayout key={`grid-${currentIndex}`} stories={gridStories} accent={accentColor} fullpage />);
                    currentIndex += 6;
                  }

                  // Last is the single one
                  if (currentIndex < remainingStories.length) {
                    layouts.push(<SingleNewsLayout key={`single-${currentIndex}`} story={remainingStories[currentIndex]} accent={accentColor} />);
                    currentIndex += 1;
                  }
                }

                return <>{layouts}</>;
              })()}
          </motion.div>
          {cta && (
            <motion.div className="flex items-center justify-center space-x-5 py-4" variants={variants}>
              {cta.map((item, index) => (
                <Button
                  key={`grid-cta-${index}`}
                  href={item?.href || '/press'}
                  color={accentColor}
                  primary={item?.style === 'primary'}
                  light={item?.style === 'light'}
                >
                  {' '}
                  {item?.label ?? 'View all news'}
                </Button>
              ))}
            </motion.div>
          )}
        </div>
      </section>
    </>
  );
};
